import type { ModelError_Item } from '@treadinc/horizon-api-spec';
import type { JSX } from 'react';
import { useFormContext } from 'react-hook-form';

import type { AddOnAssignment } from '~hooks/useAddOns/models';
import type { ContactItem } from '~hooks/useContact/models/ContactItem';
import type { MaterialRate } from '~hooks/useMaterialRates/models';
import type { Order } from '~hooks/useOrders/models';
import type { Project } from '~hooks/useProjects/models';
import type { RateBasicWithValue } from '~hooks/useRates/models';
import type { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import type { WayPoint } from '~hooks/useSites/models';
import type { User } from '~hooks/useUsers/models';
import RouteEstimateEffects from '~pages/Sales/Orders/NewOrderForm/RouteEstimateEffects';
import type { NewOrderFormSchemaInterface } from '~pages/Sales/Orders/newOrderFormSchema';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import type {
  OrderFormStateChange,
  OrderFormV3FieldValues,
  OrderWaypointDisplayValue,
  OrderWaypointSiteValue,
  TaxRateScheduleOption,
} from '../OrderFormV3Context';
import { useAgentRuntimeApi } from './useAgentRuntimeApi';
import { useAsyncErrorMapping } from './useAsyncErrorMapping';
import { useCompanyCascadeReset } from './useCompanyCascadeReset';
import { useFormStateChangeNotifier } from './useFormStateChangeNotifier';
import { useGeofenceDelaySeed } from './useGeofenceDelaySeed';
import { useLoadAtJobStartSync } from './useLoadAtJobStartSync';
import { useMaterialRateVisibility } from './useMaterialRateVisibility';
import { usePastLoadAtWarning } from './usePastLoadAtWarning';
import { usePhasesDirtySync } from './usePhasesDirtySync';
import { usePrimaryCustomerRateFetch } from './usePrimaryCustomerRateFetch';
import { useProjectAddOnFetch } from './useProjectAddOnFetch';
import { useProjectCascade } from './useProjectCascade';
import { useProjectDataFetch } from './useProjectDataFetch';
import { useProjectMaterialRatesFetch } from './useProjectMaterialRatesFetch';
import { useRecordCompanySettingsSync } from './useRecordCompanySettingsSync';
import { useSalespersonPrefillSync } from './useSalespersonPrefillSync';
import { useServiceSync } from './useServiceSync';
import { useSiteRateScheduleSync } from './useSiteRateScheduleSync';
import { useTaxRateAutopopulate } from './useTaxRateAutopopulate';
import { useTaxSectionErrorReveal } from './useTaxSectionErrorReveal';
import { useVendorRateForDriverDerivation } from './useVendorRateForDriverDerivation';
import { useVendorRateToDriverCopy } from './useVendorRateToDriverCopy';
import { useWaypointContactAutofill } from './useWaypointContactAutofill';
import { useWaypointValidationSync } from './useWaypointValidationSync';

type OrderFormV3EffectsProps = {
  defaultOrder: Order | null;
  onFormStateChange: (change: OrderFormStateChange) => void;
  // The Provider-owned AI surface the dev/test window hook re-exposes —
  // useAgentRuntimeApi installs window.__orderFormV3__ from these plus the
  // host's own form-context reads.
  applyAIPatch: (partial: Partial<NewOrderFormSchemaInterface>) => void;
  aiEditedFields: ReadonlySet<keyof NewOrderFormSchemaInterface>;
  validateField: (key: keyof NewOrderFormSchemaInterface) => Promise<boolean>;
  // Save-path 422 plumbing: the dialog's ModelError_Item[] state, the
  // Provider-memoized schema key-set the mapping matches against, and the
  // toast fallback for items no schema key claims.
  asyncErrors: ModelError_Item[] | undefined;
  schemaFieldKeys: ReadonlySet<keyof NewOrderFormSchemaInterface>;
  pushErrorAlert: (message: string) => void;
  setShowPastDateWarning: (show: boolean) => void;
  setIsMaterialRatesBlockVisible: (visible: boolean) => void;
  setIsTaxSectionVisible: (visible: boolean) => void;
  // The watched project's seed-projected externalId — the cascade copies it
  // into the read-only projectExternalId field.
  watchedProjectExternalId: string | undefined;
  // The shared post-mount latch: flipped by useProjectCascade's account
  // effect, read there now and by the waypoint contact effects.
  loaded: boolean;
  setLoaded: (loaded: boolean) => void;
  // Mode bits the rate/SRS effects key on (Provider-derived).
  isCopyAndEdit: boolean;
  isCompletedOrder: boolean;
  isEditingOrder: boolean;
  defaultVendorRateToDriver: boolean;
  // Provider-owned watches (one subscription per field — panels and modules
  // never take their own for these).
  watchedProject: OrderFormV3FieldValues['project'] | undefined;
  watchedPickUpWaypointSiteId: string | undefined;
  watchedPickUpWaypointSite: OrderWaypointSiteValue;
  watchedDropOffWaypointSite: OrderWaypointSiteValue;
  watchedCompanyId: string | undefined;
  onRecordCompanyIdChange: (companyId: string | undefined) => void;
  canPrefillSalesperson: boolean;
  itemizeMaterialRateDefaultOn: boolean;
  currentUser: User;
  watchedAccountId: string | undefined;
  watchedTaxRateSchedule: TaxRateScheduleOption | null | undefined;
  // The waypoint surface: the mount-time default waypoints the contact
  // effects guard on, the sticky pickup-divergence pair, and the setters for
  // the Provider's contact-section + display state.
  defaultPickUpWaypoint: WayPoint | undefined;
  defaultDropOffWaypoint: WayPoint | undefined;
  pickupChangedFromSaved: boolean;
  setPickupChangedFromSaved: (changed: boolean) => void;
  setShowPickUpContact: (show: boolean) => void;
  setShowDropOffContact: (show: boolean) => void;
  setPickUpContacts: (contacts: ContactItem[]) => void;
  setDropOffContacts: (contacts: ContactItem[]) => void;
  setLastSelectedPickUpPhone: (phone: string) => void;
  setLastSelectedDropOffPhone: (phone: string) => void;
  setPickUpWaypoint: (
    update: (previous: OrderWaypointDisplayValue) => OrderWaypointDisplayValue,
  ) => void;
  setDropOffWaypoint: (
    update: (previous: OrderWaypointDisplayValue) => OrderWaypointDisplayValue,
  ) => void;
  // Provider state the modules read and/or write (values + setters travel
  // as props so the module graph stays cycle-free).
  projectData: Project | null;
  setProjectData: (project: Project | null) => void;
  setProjectMaterialRates: (rates: MaterialRate[]) => void;
  projectHasSiteRates: boolean;
  setProjectHasSiteRates: (has: boolean) => void;
  projectSiteRates: SiteRateSchedule | undefined;
  setProjectSiteRates: (rates: SiteRateSchedule | undefined) => void;
  completedOrderRules: SiteRateSchedule[];
  setCompletedOrderRules: (rules: SiteRateSchedule[]) => void;
  setCompletedOrderRulesLoaded: (loaded: boolean) => void;
  setProjectHasSiteRatesLoaded: (loaded: boolean) => void;
  setOrderSiteRateSchedules: (rules: SiteRateSchedule[]) => void;
  setSiteRateCustomerAddOnAssignments: (a: AddOnAssignment[] | undefined) => void;
  setSiteRateVendorAddOnAssignments: (a: AddOnAssignment[] | undefined) => void;
  setSiteRateDriverAddOnAssignments: (a: AddOnAssignment[] | undefined) => void;
  setProjectCustomerAddOnAssignments: (a: AddOnAssignment[] | undefined) => void;
  setProjectVendorAddOnAssignments: (a: AddOnAssignment[] | undefined) => void;
  setProjectDriverAddOnAssignments: (a: AddOnAssignment[] | undefined) => void;
  setUseVendorRateForDriver: (value: boolean) => void;
  setIsFreightRatesBlockVisible: (visible: boolean) => void;
  // Preload plumbing (state lives in useOrderFormV3Preloads).
  primaryCustomerRate: RateBasicWithValue | null;
  loadPrimaryCustomerRate: (args: {
    accountId: string;
    companyId: string;
  }) => Promise<void>;
  clearPrimaryCustomerRate: () => void;
  geofenceDelayDefault: number | null;
  // Initial values for the legacy RouteEstimateEffects render: projections of
  // the CONCRETE setDefaultOrderValues return — the schema interface can't
  // type them, so the Provider projects them on its side of the useForm
  // boundary cast (see materialRateInitialIds).
  initialCycleDistance: number | undefined;
  initialCycleTime: number | undefined;
  initialPickupToDropoff: number | undefined;
};

/**
 * Render-less host for every Provider effect: each behavior lives in ONE
 * named module under effects/ (standard header: Triggers / Writes / Guards /
 * Legacy / Test), and this component calls the modules in FIXED, EXPLICIT
 * order — one call per line, never a loop over hook arrays — so the file
 * doubles as the in-code effect index. The Provider mounts it inside
 * <FormProvider>, above {children}; each later addition appends exactly one
 * call line (or one render line, e.g. <RouteEstimateEffects/>). Inputs
 * arrive as props/args rather than through useOrderFormV3Context so the
 * module graph stays cycle-free.
 */
export function OrderFormV3Effects({
  defaultOrder,
  onFormStateChange,
  applyAIPatch,
  aiEditedFields,
  validateField,
  asyncErrors,
  schemaFieldKeys,
  pushErrorAlert,
  setShowPastDateWarning,
  setIsMaterialRatesBlockVisible,
  setIsTaxSectionVisible,
  watchedProjectExternalId,
  loaded,
  setLoaded,
  isCopyAndEdit,
  isCompletedOrder,
  isEditingOrder,
  defaultVendorRateToDriver,
  watchedProject,
  watchedPickUpWaypointSiteId,
  watchedPickUpWaypointSite,
  watchedDropOffWaypointSite,
  watchedCompanyId,
  onRecordCompanyIdChange,
  canPrefillSalesperson,
  itemizeMaterialRateDefaultOn,
  currentUser,
  watchedAccountId,
  watchedTaxRateSchedule,
  defaultPickUpWaypoint,
  defaultDropOffWaypoint,
  pickupChangedFromSaved,
  setPickupChangedFromSaved,
  setShowPickUpContact,
  setShowDropOffContact,
  setPickUpContacts,
  setDropOffContacts,
  setLastSelectedPickUpPhone,
  setLastSelectedDropOffPhone,
  setPickUpWaypoint,
  setDropOffWaypoint,
  projectData,
  setProjectData,
  setProjectMaterialRates,
  projectHasSiteRates,
  setProjectHasSiteRates,
  projectSiteRates,
  setProjectSiteRates,
  completedOrderRules,
  setCompletedOrderRules,
  setCompletedOrderRulesLoaded,
  setProjectHasSiteRatesLoaded,
  setOrderSiteRateSchedules,
  setSiteRateCustomerAddOnAssignments,
  setSiteRateVendorAddOnAssignments,
  setSiteRateDriverAddOnAssignments,
  setProjectCustomerAddOnAssignments,
  setProjectVendorAddOnAssignments,
  setProjectDriverAddOnAssignments,
  setUseVendorRateForDriver,
  setIsFreightRatesBlockVisible,
  primaryCustomerRate,
  loadPrimaryCustomerRate,
  clearPrimaryCustomerRate,
  geofenceDelayDefault,
  initialCycleDistance,
  initialCycleTime,
  initialPickupToDropoff,
}: OrderFormV3EffectsProps): JSX.Element {
  const { control, getFieldState, getValues, setError, setValue, trigger } =
    useFormContext<OrderFormV3FieldValues>();

  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const formCompanyId = watchedCompanyId || companyId;

  useFormStateChangeNotifier({ control, onFormStateChange });
  useAgentRuntimeApi({
    applyAIPatch,
    aiEditedFields,
    validateField,
    getValues,
    getFieldState,
  }); // installs the window.__orderFormV3__ dev/test agent runtime surface
  useAsyncErrorMapping({
    errors: asyncErrors,
    schemaFieldKeys,
    setError,
    pushErrorAlert,
  }); // ports ProjectDialog's inline-422 parity (a legacy gap)
  useCompanyCascadeReset({ control, defaultOrder, getValues, setValue });
  useRecordCompanySettingsSync({ watchedCompanyId, onRecordCompanyIdChange });
  useSalespersonPrefillSync({
    defaultOrder,
    canPrefillSalesperson,
    currentUser,
    getFieldState,
    getValues,
    setValue,
  });
  useServiceSync({ defaultOrder, setValue });
  useLoadAtJobStartSync({ control, setValue });
  usePastLoadAtWarning({ control, defaultOrder, setShowPastDateWarning });
  useMaterialRateVisibility({ control, setIsMaterialRatesBlockVisible });
  useTaxRateAutopopulate({ watchedTaxRateSchedule, getValues, setValue });
  useTaxSectionErrorReveal({ control, setIsTaxSectionVisible });
  useSiteRateScheduleSync({
    dispatchAsCompanyId: companyId,
    formCompanyId,
    watchedProject,
    watchedPickUpWaypointSiteId,
    defaultOrder,
    isCopyAndEdit,
    isCompletedOrder,
    projectData,
    primaryCustomerRate,
    projectSiteRates,
    projectHasSiteRates,
    completedOrderRules,
    setProjectHasSiteRates,
    setProjectSiteRates,
    setCompletedOrderRules,
    setCompletedOrderRulesLoaded,
    setProjectHasSiteRatesLoaded,
    setOrderSiteRateSchedules,
    setSiteRateCustomerAddOnAssignments,
    setSiteRateVendorAddOnAssignments,
    setSiteRateDriverAddOnAssignments,
    setIsFreightRatesBlockVisible,
  }); // SRS fetch, match, auto-visibility, and presence resolution
  useProjectDataFetch({ watchedProject, setProjectData });
  useProjectCascade({
    control,
    setValue,
    defaultOrder,
    projectData,
    watchedProjectExternalId,
    loaded,
    setLoaded,
    setIsTaxSectionVisible,
    itemizeMaterialRateDefaultOn,
  }); // the project-change cascade (defaults, metadata, phase/totals, department)
  usePhasesDirtySync({ control, defaultOrder, setValue }); // re-dirties `phases` when the embedded phase-drawer edits it without flagging dirty
  useProjectAddOnFetch({
    companyId: formCompanyId,
    watchedProject,
    defaultOrder,
    setProjectCustomerAddOnAssignments,
    setProjectVendorAddOnAssignments,
    setProjectDriverAddOnAssignments,
  });
  useProjectMaterialRatesFetch({
    watchedProject,
    watchedCompanyId,
    watchedAccountId,
    setProjectMaterialRates,
  }); // publishes the project's material rates for the materials card to print
  useVendorRateForDriverDerivation({
    projectData,
    isEditingOrder,
    defaultVendorRateToDriver,
    setUseVendorRateForDriver,
  });
  useVendorRateToDriverCopy({ projectSiteRates, setUseVendorRateForDriver });
  usePrimaryCustomerRateFetch({
    control,
    isEditingOrder,
    loadPrimaryCustomerRate,
    clearPrimaryCustomerRate,
  });
  useGeofenceDelaySeed({ defaultOrder, geofenceDelayDefault, setValue });
  useWaypointValidationSync({
    control,
    defaultOrder,
    defaultPickUpWaypoint,
    defaultDropOffWaypoint,
    watchedPickUpWaypointSite,
    watchedDropOffWaypointSite,
    trigger,
    setValue,
    pickupChangedFromSaved,
    setPickupChangedFromSaved,
    setShowPickUpContact,
    setShowDropOffContact,
  });
  useWaypointContactAutofill({
    loaded,
    watchedCompanyId,
    watchedPickUpWaypointSite,
    watchedDropOffWaypointSite,
    defaultPickUpWaypoint,
    defaultDropOffWaypoint,
    isEditingOrder,
    setPickUpContacts,
    setDropOffContacts,
    setShowPickUpContact,
    setShowDropOffContact,
    setLastSelectedPickUpPhone,
    setLastSelectedDropOffPhone,
    setPickUpWaypoint,
    setDropOffWaypoint,
    setValue,
  });

  // The render-less RouteEstimateEffects drives cycleDistance/cycleTime/
  // pickupToDropoff from map geometry. It needs the form context the
  // Provider owns, so the host renders it as-is — an effect surface, not
  // UI; it returns null — instead of rewriting its internals as a module.
  return (
    <RouteEstimateEffects
      initialCycleDistance={initialCycleDistance}
      initialCycleTime={initialCycleTime}
      initialPickupToDropoff={initialPickupToDropoff}
    />
  );
}
